import { normalizeComposeSlots, type ComposePlan } from "./types";

function normalizeLoadedPlan(parsed: ComposePlan): ComposePlan {
  return {
    ...parsed,
    confirmedLinks: parsed.confirmedLinks ?? [],
    matchBoosts: parsed.matchBoosts ?? [],
    archetype: {
      ...parsed.archetype,
      slots: normalizeComposeSlots(parsed.archetype?.slots),
      sizeAxisHint: parsed.archetype?.sizeAxisHint ?? "",
      skuListRaw: parsed.archetype?.skuListRaw ?? "",
    },
  };
}

const PREFIX = "mfg-compose-plan:";

function storage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

export function composePlanStorageKey(orgId: string, planId: string): string {
  return `${PREFIX}${orgId}:${planId}`;
}

export function composePlanIndexKey(orgId: string): string {
  return `${PREFIX}index:${orgId}`;
}

export type ComposePlanSummary = {
  planId: string;
  seriesName: string;
  seriesCode: string;
  entryMode: ComposePlan["entryMode"];
  step: ComposePlan["step"];
  updatedAt: string;
  productCount: number;
  committedProducts: number;
  readyProducts: number;
  /** Remote draft status when loaded from org store. */
  status?: "open" | "parked" | "completed" | "cancelled";
  updatedByUserId?: string | null;
};

export function saveComposePlan(plan: ComposePlan): void {
  const store = storage();
  if (!store) return;
  const key = composePlanStorageKey(plan.orgId, plan.planId);
  const payload = JSON.stringify({ ...plan, updatedAt: new Date().toISOString() });
  store.setItem(key, payload);
  const indexKey = composePlanIndexKey(plan.orgId);
  const raw = store.getItem(indexKey);
  const ids: string[] = raw ? (JSON.parse(raw) as string[]) : [];
  if (!ids.includes(plan.planId)) {
    ids.push(plan.planId);
    store.setItem(indexKey, JSON.stringify(ids));
  }
}

export function loadComposePlan(orgId: string, planId: string): ComposePlan | null {
  const store = storage();
  if (!store) return null;
  const raw = store.getItem(composePlanStorageKey(orgId, planId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ComposePlan;
    if (parsed?.version !== 1 || parsed.orgId !== orgId) return null;
    return normalizeLoadedPlan(parsed);
  } catch {
    return null;
  }
}

export function clearComposePlan(orgId: string, planId: string): void {
  const store = storage();
  if (!store) return;
  store.removeItem(composePlanStorageKey(orgId, planId));
  const indexKey = composePlanIndexKey(orgId);
  const raw = store.getItem(indexKey);
  if (!raw) return;
  try {
    const ids = (JSON.parse(raw) as string[]).filter((id) => id !== planId);
    store.setItem(indexKey, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export function listComposePlanIds(orgId: string): string[] {
  const store = storage();
  if (!store) return [];
  const raw = store.getItem(composePlanIndexKey(orgId));
  if (!raw) return [];
  try {
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

export function listComposePlanSummaries(orgId: string): ComposePlanSummary[] {
  const ids = listComposePlanIds(orgId);
  const out: ComposePlanSummary[] = [];
  for (const planId of ids) {
    const plan = loadComposePlan(orgId, planId);
    if (!plan) continue;
    const products = plan.nodes.filter((n) => n.role === "product");
    const committedProducts = products.filter((n) => n.match.status === "matched").length;
    // ready count without importing commitPlan (avoid cycle) — approximate later in UI
    out.push({
      planId: plan.planId,
      seriesName: plan.archetype.seriesName,
      seriesCode: plan.archetype.seriesCode,
      entryMode: plan.entryMode,
      step: plan.step,
      updatedAt: plan.updatedAt,
      productCount: products.length,
      committedProducts,
      readyProducts: 0,
    });
  }
  out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return out;
}

export function exportComposePlanJson(plan: ComposePlan): string {
  return JSON.stringify(plan, null, 2);
}

export function importComposePlanJson(raw: string, orgId: string): ComposePlan | null {
  try {
    const parsed = JSON.parse(raw) as ComposePlan;
    if (parsed?.version !== 1) return null;
    return {
      ...normalizeLoadedPlan(parsed),
      orgId,
      updatedAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}
